<template>
  <v-container id="user-profile" tag="section">
    <v-row class="mt-4">
      <v-col cols="6">
        <v-card>
          <v-row>
            <v-col cols="9">
              <v-card-title
                >{{ user.first_name }} {{ user.last_name }}</v-card-title
              >
              <v-card-subtitle
                >Username: {{ user.username }} - Email:
                {{ user.email }}</v-card-subtitle
              >
            </v-col>
            <v-col cols="3">
              <v-btn text color="primary" @click="editProfile = !editProfile"
                >Update</v-btn
              >
            </v-col>
          </v-row>
          <div v-if="editProfile">
            <v-card-text>
              <form lazy-validation>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="First Name"
                      :min="3"
                      :counter="25"
                      v-model="user.first_name"
                      :rules="[required, min3]"
                    ></v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      label="Last Name"
                      :min="3"
                      :counter="25"
                      v-model="user.last_name"
                      :rules="[required, min3]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="Title"
                      :counter="100"
                      v-model="user.title"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="Institution"
                      :counter="100"
                      v-model="user.department"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="Address"
                      :counter="100"
                      v-model="user.location"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-text-field
                      label="Homepage"
                      :counter="100"
                      v-model="user.homepage"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-text-field
                      label="CRSid"
                      hint="University of Cambridge Raven identifier - lets you log in via the CRSid (CAMCEAD members only) option on the sign-in page"
                      persistent-hint
                      :counter="20"
                      v-model="user.crsid"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </form>
            </v-card-text>
            <v-divider></v-divider>
            <v-alert v-if="account_error" type="error" dense text dismissible
              class="mx-4 mt-4" @input="account_error = ''">{{ account_error }}</v-alert>
            <v-card-actions>
              <v-btn
                :loading="loading_account"
                color="primary"
                @click="updateAccount()"
              >
                Update
              </v-btn>
              <v-snackbar
                :timeout="3000"
                v-model="snackbar_user"
                color="success"
                absolute
                right
                top
              >
                User updated
              </v-snackbar>
            </v-card-actions>
          </div>
        </v-card>

        <v-card class="mt-6">
          <v-card-title>JupyterHub Version</v-card-title>
          <v-card-text>
            <p>
              Select the JupyterHub version to use for your server. You can
              change this option at any time. Note that certain images may not
              be compatible with all JupyterHub versions. To choose which
              site (ARCUS/Imperial/STFC) a new server runs on, use the site
              selector on the server start page instead.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-select
              v-model="jh_version"
              :items="jh_versions"
              dense
              solo
            ></v-select>
          </v-card-actions>
        </v-card>

        <v-card class="mt-6" v-if="!user.otp_verified">
          <v-card-title>Multi factor authentication</v-card-title>
          <v-card-text>
            <p>
              Enable two-factor authentication to increase the security of your
              account.
            </p>
            <!-- <v-row>
              <v-col>
                <v-switch
                  v-model="user.otp_enabled"
                  label="Enable two-factor authentication"
                ></v-switch>
              </v-col>
            </v-row> -->
            <v-row align="center">
              <v-col class="pa-6 text-center">
                <q-r-code :size="192" :text="user.otp_auth_url" />
              </v-col>
            </v-row>
            <p>
              Scan the QR code with your authenticator app (e.g. Google
              Authenticator) and type the code below.
            </p>
            <v-form ref="otpForm" lazy-validation>
              <v-row>
                <v-col>
                  <v-text-field
                    label="Authentication Code"
                    :min="0"
                    :counter="10"
                    v-model="otp"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-btn
                    color="primary"
                    @click="otpVerify()"
                    :loading="loading_otp"
                  >
                    Verify
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
        <v-card class="mt-6" v-else>
          <v-card-title>Multi factor authentication</v-card-title>
          <v-card-text>
            <p>
              Two-factor authentication is enabled for your account. This cannot
              be disabled.
            </p>
            <p>
              Setting up a new device (e.g. you got a new phone, or lost access
              to your authenticator app)? Resetting will invalidate your
              current authenticator entry — you'll need to scan a new QR code
              and re-verify before you can sign in with an authenticator code
              again.
            </p>
            <v-btn color="error" outlined @click="resetOtp()" :loading="loading_otp">
              Reset two-factor authentication
            </v-btn>
            <v-snackbar
              :timeout="4000"
              v-model="snackbar_otp_reset"
              color="success"
              absolute
              right
              top
            >
              Two-factor authentication reset — scan the new QR code below
            </v-snackbar>
          </v-card-text>
        </v-card>

        <v-card class="mt-6">
          <v-row>
            <v-col cols="9">
              <v-card-title>Password change</v-card-title>
            </v-col>
            <v-col cols="3">
              <v-btn text color="primary" @click="editPassword = !editPassword"
                >Show</v-btn
              >
            </v-col>
          </v-row>
          <div v-if="editPassword">
            <v-card-text>
              <v-form
                ref="passwordForm"
                v-model="passwordValid"
                lazy-validation
              >
                <v-row>
                  <v-col>
                    <v-text-field
                      label="Current password"
                      hint="At least 8 characters"
                      :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="togglepass ? 'text' : 'password'"
                      :min="0"
                      :counter="25"
                      v-model="password0"
                      :rules="[required, min8]"
                      @click:append="togglepass = !togglepass"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="New password"
                      hint="At least 8 characters"
                      :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="togglepass ? 'text' : 'password'"
                      :min="0"
                      :counter="25"
                      v-model="password1"
                      :rules="[strength, required, min8]"
                      @click:append="togglepass = !togglepass"
                    ></v-text-field>
                    Password strength:
                    <v-progress-linear
                      :color="score.color"
                      :value="score.value"
                    ></v-progress-linear>
                  </v-col>
                  <v-col>
                    <v-text-field
                      label="Password confirmation"
                      hint="At least 8 characters"
                      :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="togglepass ? 'text' : 'password'"
                      :min="0"
                      :counter="25"
                      v-model="password2"
                      :rules="[required, min8, matchingPasswords]"
                      @click:append="togglepass = !togglepass"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                :disabled="!this.passwordValid"
                color="primary"
                :loading="loading_password"
                @click="passwordChange()"
              >
                Update
              </v-btn>
              <v-snackbar
                :timeout="3000"
                v-model="snackbar_password"
                :color="snackbar_password_color"
                absolute
                right
                top
              >
                {{ snackbar_password_msg_text }}
              </v-snackbar>
            </v-card-actions>
          </div>
        </v-card>
      </v-col>

      <v-col cols="6">
        <v-card class="mt-6">
          <v-card-title>Jupyter API Access</v-card-title>
          <v-card-text>
            <p>
              The access token allows external access to your Jupyter Server. It
              can be used e.g. to develop the notebooks locally and execute them
              in the server.
            </p>

            <div v-if="user.jupyter_access_token">
              <p>
                Your unique access token is displayed below. Click on it to copy
                it to the clipboard.
              </p>

              <v-btn
                outlined
                @click="copyToClipBoard(user.jupyter_access_token)"
              >
                {{
                  togglejupyter
                    ? mask(user.jupyter_access_token)
                    : user.jupyter_access_token
                }}
                <!-- <v-icon right>mdi-content-copy</v-icon> -->
              </v-btn>
              <v-btn icon small @click="togglejupyter = !togglejupyter">
                <v-icon right>mdi-eye</v-icon>
              </v-btn>
            </div>
            <div v-else>
              <p>
                You don't have an access token yet. Click the button below to
                create one.
              </p>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              v-if="!user.jupyter_access_token"
              color="primary"
              @click="createAccessToken"
            >
              Create Token
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card class="mt-6">
          <v-card-title>S3 / MinIO Storage Access</v-card-title>
          <v-card-text>
            <p>
              The <router-link :to="{ name: 'Storage' }">Storage</router-link> page is the
              easiest way to browse, upload, download, and share your files. Use these
              credentials instead if you want direct S3 access - from your own machine with a
              client like <code>mc</code>, <code>boto3</code>, or CyberDuck, or from a script.
              They're also pre-configured automatically inside every Jupyter server you start.
            </p>
            <p>
              Endpoint: <code>https://camceadhub.ast.cam.ac.uk:8094</code
              ><br />
              Access Key: <code>{{ user.username }}</code>
            </p>

            <div v-if="user.s3_access_token">
              <p>
                Secret Key is displayed below. Click on it to copy it to the
                clipboard.
              </p>

              <v-btn outlined @click="copyToClipBoard(user.s3_access_token)">
                {{ toggles3 ? mask(user.s3_access_token) : user.s3_access_token }}
              </v-btn>
              <v-btn icon small @click="toggles3 = !toggles3">
                <v-icon right>mdi-eye</v-icon>
              </v-btn>
            </div>
            <div v-else>
              <p>
                Your Secret Key is created automatically the next time you
                log in.
              </p>
            </div>
          </v-card-text>
        </v-card>

        <casper-access-card></casper-access-card>

        <v-card class="mt-6">
          <v-card-title>
            GitHub Account &amp; VS Code Tunnel
            <v-chip v-if="github.connected" small color="green" dark class="ml-2"
              >linked</v-chip
            >
          </v-card-title>
          <v-card-text>
            <v-alert
              v-if="githubMessage"
              :type="githubMessageType"
              dense
              text
              dismissible
              @input="githubMessage = ''"
              >{{ githubMessage }}</v-alert
            >
            <div v-if="!github.connected">
              <p>
                Link your GitHub account to identify yourself for VS Code
                Remote Tunnels, so your local VS Code can connect directly to
                your JupyterLab server's resources.
              </p>
              <v-btn color="primary" :loading="loading_github" @click="connectGitHub">
                <v-icon left>mdi-github</v-icon>Connect GitHub
              </v-btn>
            </div>
            <div v-else>
              <p>
                Linked to GitHub account
                <strong>{{ github.github_username }}</strong>. Your tunnel
                machine name is <code>{{ github.tunnel_name }}</code>.
              </p>

              <v-card-title class="pl-0">One-time setup</v-card-title>
              <ol>
                <li>
                  Open a Terminal from any of your JupyterLab servers and run:
                  <br /><code>vscode-tunnel</code>
                </li>
                <li>
                  It will print a URL and a one-time code - open the URL and
                  enter the code, signing in with
                  <strong>{{ github.github_username }}</strong
                  >.
                </li>
                <li>
                  That's it. This only needs to be done once - every server
                  you start from now on will automatically expose a tunnel
                  named <code>{{ github.tunnel_name }}</code>.
                </li>
              </ol>

              <v-card-title class="pl-0">Connecting from your local VS Code</v-card-title>
              <ol>
                <li>
                  Install the "Remote - Tunnels" extension (or open
                  <a href="https://vscode.dev" target="_blank">vscode.dev</a>).
                </li>
                <li>
                  Sign in with the <strong>same GitHub account</strong> via
                  the Accounts icon in the bottom-left corner.
                </li>
                <li>
                  Open the Remote Explorer view, select "Tunnels", and pick
                  <code>{{ github.tunnel_name }}</code> once your server is
                  running.
                </li>
              </ol>

              <v-btn
                text
                small
                color="error"
                class="mt-2"
                :loading="loading_github"
                @click="disconnectGitHub"
              >
                Disconnect GitHub
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  ACCOUNTS_GITHUB_AUTHORIZE,
  ACCOUNTS_GITHUB_STATUS,
  API,
  GENERATE_OTP,
  VERIFY_OTP,
} from "@/axios-config";
import QRCode from "@/components/QRCode";
import CasperAccessCard from "@/components/CasperAccessCard";
import { useUserStore } from "@/store/user";
import { HUB_VERSIONS } from "@/config/jupyterhubs";
import zxcvbn from "zxcvbn";

export default {
  components: { QRCode, CasperAccessCard },
  name: "ProfileEdit",
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  data() {
    return {
      editProfile: false,
      editPassword: false,
      toggles3: 1,
      togglejupyter: 1,
      announcements: [],
      passwordValid: true,
      user: {},
      profile: {},
      servers: [],
      jobs: [],
      ready: false,
      overlay: true,
      togglepass: false,
      loading_account: false,
      account_error: "",
      password0: "",
      password1: "",
      password2: "",
      snackbar_user: false,
      snackbar_otp_reset: false,
      snackbar_password: false,
      snackbar_password_color: "success",
      snackbar_password_error: false,
      snackbar_password_error_text: "",
      snackbar_password_msg_text: "",
      loading_password: false,
      loading_otp: false,
      otp: "",
      jh_version: "",
      jh_versions: HUB_VERSIONS.map((v) => v.label),
      github: { connected: false, github_username: null, tunnel_name: "" },
      loading_github: false,
      githubMessage: "",
      githubMessageType: "success",
      headers: [
        { text: "Job", value: "id" },
        { text: "Name", value: "name" },
        { text: "Dataset", value: "dataset" },
        { text: "Created", value: "createdOn" },
        { text: "Status", value: "status" },
      ],
    };
  },
  computed: {
    score() {
      const result = zxcvbn(this.password1);

      switch (result.score) {
        case 4:
          return {
            color: "light-blue",
            value: 100,
          };
        case 3:
          return {
            color: "light-green",
            value: 75,
          };
        case 2:
          return {
            color: "yellow",
            value: 50,
          };
        case 1:
          return {
            color: "orange",
            value: 25,
          };
        default:
          return {
            color: "red",
            value: 0,
          };
      }
    },
  },

  watch: {
    jh_version(newVersion) {
      const match = HUB_VERSIONS.find((v) => v.label === newVersion);
      this.userStore.setJupyterHubVersion(match ? match.id : HUB_VERSIONS[0].id);
    },
  },

  methods: {
    required: function (value) {
      if (value) {
        return true;
      } else {
        return "This field is required.";
      }
    },
    min8: function (value) {
      if (value.length >= 8) {
        return true;
      } else {
        return "Password should have more than 8 characters.";
      }
    },
    min3: function (value) {
      if (!value) {
        return false;
      }
      if (value.length >= 3) {
        return true;
      } else {
        return "Password should have more than 3 characters.";
      }
    },
    matchingPasswords: function () {
      if (this.password1 === this.password2) {
        return true;
      } else {
        return "Passwords does not match.";
      }
    },
    strength: function (value) {
      const score = zxcvbn(value).score;
      if (score >= 3) {
        return true;
      } else {
        return "Password is too weak.";
      }
    },
    mask(val) {
      return val.replace(/./g, "*");
    },
    async createAccessToken() {
      try {
        const { data } = await API.post("/api/v1/accounts/jupyter-token/");
        this.user.jupyter_access_token = data.token;
      } catch (error) {
        console.log(error);
      }
    },
    async aboutMe() {
      try {
        const { data } = await API.get("/api/v1/accounts/me/");
        this.user = data;
      } catch (error) {
        console.log(error);
      }
    },
    async updateAccount() {
      this.loading_account = true;
      this.account_error = "";
      try {
        await API.post("/api/v1/accounts/me/", {
          first_name: this.user.first_name,
          last_name: this.user.last_name,
          department: this.user.department,
          location: this.user.location,
          title: this.user.title,
          homepage: this.user.homepage,
          bio: this.user.bio,
          crsid: this.user.crsid,
        });
        this.snackbar_user = true;
      } catch (error) {
        this.account_error =
          error.response?.data?.detail || "Could not update your profile.";
      }
      this.loading_account = false;
    },
    async passwordChange() {
      this.loading_password = true;
      try {
        await API.post("/api/v1/accounts/password/change/", {
          old_password: this.password0,
          new_password1: this.password1,
          new_password2: this.password2,
        });
        this.snackbar_password_color = "success";
        this.snackbar_password_msg_text = "Password changed";
        this.snackbar_password = true;
      } catch (error) {
        console.log(error);
        this.snackbar_password_color = "error";
        this.snackbar_password_msg_text = error.response.data.detail;
        this.snackbar_password = true;
      }
      this.loading_password = false;
      // this.$refs.passwordForm.reset();
    },
    copyToClipBoard(textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    },
    async githubStatus() {
      try {
        const { data } = await API.get(ACCOUNTS_GITHUB_STATUS);
        this.github = data;
      } catch (error) {
        console.log(error);
      }
    },
    async connectGitHub() {
      this.loading_github = true;
      try {
        const { data } = await API.get(ACCOUNTS_GITHUB_AUTHORIZE);
        window.location.href = data.url;
      } catch (error) {
        console.log(error);
        this.githubMessage = "Could not start GitHub linking. Please try again.";
        this.githubMessageType = "error";
        this.loading_github = false;
      }
    },
    async disconnectGitHub() {
      this.loading_github = true;
      try {
        await API.delete(ACCOUNTS_GITHUB_STATUS);
        await this.githubStatus();
      } catch (error) {
        console.log(error);
      }
      this.loading_github = false;
    },
    otpVerify() {
      this.loading_otp = true;
      API.post(VERIFY_OTP, {
        uuid: this.user.uuid,
        token: this.otp,
      })
        .then((response) => {
          console.log(response.data);
          this.userStore.setToken(response.data.access);
          this.userStore.setRefreshToken(response.data.refresh);
          this.user.otp_verified = true;
          this.userStore.setOTP(true);
          this.snackbar_user = true;
        })
        .catch((error) => {
          console.log(error);
        });
      this.loading_otp = false;
    },
    resetOtp() {
      if (
        !confirm(
          "This invalidates your current authenticator app entry. You'll need to scan a new QR code and re-verify before you can sign in with an authenticator code again. Continue?"
        )
      ) {
        return;
      }
      this.loading_otp = true;
      API.post(GENERATE_OTP)
        .then((response) => {
          this.user.otp_auth_url = response.data.otpauth_url;
          this.user.otp_verified = false;
          this.otp = "";
          this.snackbar_otp_reset = true;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading_otp = false;
        });
    },
  },
  mounted: function () {
    this.$nextTick(function () {
      this.aboutMe();
      this.githubStatus();
      const currentVersion = HUB_VERSIONS.find(
        (v) => v.id === this.userStore.getJupyterHubVersion
      );
      this.jh_version = currentVersion ? currentVersion.label : HUB_VERSIONS[0].label;
    });

    const githubResult = this.$route.query.github;
    if (githubResult === "connected") {
      this.githubMessage = "GitHub account linked successfully.";
      this.githubMessageType = "success";
    } else if (githubResult === "error") {
      this.githubMessage = "Could not link your GitHub account. Please try again.";
      this.githubMessageType = "error";
    }
    if (githubResult) {
      this.$router.replace({ query: {} });
    }
  },
};
</script>